import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const AccessCodeForm = ({ ctaLabel = "Start my assessment" }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [code, setCode] = useState(() => (params.get("code") || "").toUpperCase());
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { const c = params.get("code"); if (c) setCode(c.toUpperCase()); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !password.trim()) { toast.error("Enter your access code and password."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/code-login", { code: code.trim(), password: password.trim() });
      setUser(res.data);
      toast.success(res.data.resumed ? "Welcome back! Picking up where you left off." : "Welcome! Let's begin.");
      navigate("/dashboard", { state: { user: res.data } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Check your code and password.");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="access-code-form">
      <div>
        <Label htmlFor="code" className="font-heading font-bold text-slate-800">Access code</Label>
        <Input id="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="RW-XXXX-XXXX" className="mt-1.5 rounded-xl font-mono tracking-wide"
          data-testid="access-code-input" />
      </div>
      <div>
        <Label htmlFor="pwd" className="font-heading font-bold text-slate-800">Password</Label>
        <Input id="pwd" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Your one-time password" className="mt-1.5 rounded-xl"
          data-testid="access-password-input" />
      </div>
      <Button type="submit" disabled={busy} data-testid="access-submit-btn"
        className="w-full rounded-xl h-12 font-heading font-bold transition-transform hover:scale-[1.01] active:scale-[0.99]">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
        {ctaLabel} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    </form>
  );
};
